import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next/typescript/t";

import { useResetPasswordMutation } from "@/redux/service";
import { handleErrors } from "@/shared/utils/forms";
import { HookFormInput } from "@/components_v2/FormFields/ReactHookForm/fields";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import BaseButton from "@/components_v2/Button/BaseButton";
import { ResetPasswordFormValues } from "./types";

const resetPasswordSchema = (
  t: TFunction,
): yup.ObjectSchema<ResetPasswordFormValues> =>
  yup.object().shape({
    email: yup
      .string()
      .typeError(t("auth.validation.emailNotValid"))
      .email(t("auth.validation.enterValidEmail"))
      .required(t("auth.validation.enterValidEmail")),
  });

const ResetPassword = () => {
  const navigate = useNavigate();
  const { notification } = App.useApp();
  const [resetPassword, { isLoading, isSuccess }] = useResetPasswordMutation();
  const { t } = useTranslation();

  const form = useForm<ResetPasswordFormValues>({
    resolver: yupResolver(resetPasswordSchema(t)),
    mode: "onChange",
  });

  const onSubmit = (values: ResetPasswordFormValues) => {
    resetPassword(values)
      .unwrap()
      .catch((errors) => {
        if (
          errors?.status === 429 ||
          errors?.data?.errors?.[0]?.code === "throttled"
        ) {
          notification.error({
            message: t("auth.messages.resetPasswordThrottled"),
          });
          return;
        }
        handleErrors(errors, form.setError, notification);
        if (errors?.data?.errors[0]?.code === "email_verification") {
          navigate("/email-verification");
        }
      });
  };

  return (
    <div className="flex flex-col gap-400">
      <h2 className="header-l-medium text-center">
        {t("auth.labels.resetPassword")}
      </h2>

      {isSuccess ? (
        <h4 className="header-m-medium text-center">
          {t("auth.messages.successChangePassword")} <br />
          {t("auth.messages.successChangePasswordDetails")}
        </h4>
      ) : (
        <div className="form-input-container">
          <FormFieldWrapper label={t("auth.labels.email")} required>
            <HookFormInput
              name="email"
              control={form.control}
              placeholder={t("auth.placeholders.enterYourEmail")}
            />
          </FormFieldWrapper>

          <div className="flex flex-col items-center">
            <BaseButton
              disabled={isLoading}
              isFullWidth
              onClick={form.handleSubmit(onSubmit)}
            >
              {t("auth.labels.submit")}
            </BaseButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResetPassword;
